import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {LayerWeightSockets} from "#material/nodes/shader/layer-weight/definition"
import {layerWeightDefinition} from "#material/nodes/shader/layer-weight/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeConvert, threeValue, threeVec3} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import {cyclesVec3} from "#material/cycles/helpers"
import type {ThreeReturns} from "#material/sockets/three"
import type {LayerWeightNode} from "#material/cycles/types"

@registerMaterialGraphNode
export class LayerWeight extends MaterialNode<LayerWeightSockets> {
    public override get definition() {
        return layerWeightDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<LayerWeightSockets>> => {
        const {blend, normal} = await evaluator.getAll({
            blend: this.parameters.inputs.blend,
            normal: this.parameters.inputs.normal,
        })

        const blendValue = blend?.toFloat() ?? threeConvert(this.parameters.settings.blend, threeValue) ?? threeValue(0.5)
        const normalValue = normal?.toVec3() ?? threeConvert(this.parameters.settings.normal, threeVec3) ?? TSL.normalView

        const facing = TSL.float(1.0)
            .sub(TSL.dot(TSL.normalize(TSL.positionViewDirection), TSL.normalize(normalValue)).abs())
            .pow(TSL.float(2.0))

        const fresnel = TSL.mix(TSL.float(0.0), facing, blendValue)

        return {fresnel, facing}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<LayerWeightSockets>> => {
        const layerWeightNode: LayerWeightNode = {
            type: "layer_weight",
            inputs: await evaluator.getAll({
                blend: this.parameters.inputs.blend,
                normal: this.parameters.inputs.normal,
            }),
            parameters: {blend: this.parameters.settings.blend, normal: cyclesVec3(this.parameters.settings.normal)},
            resources: {},
        }

        return {facing: [layerWeightNode, "facing"], fresnel: [layerWeightNode, "fresnel"]}
    }
}
